import { DatePickerField } from '@campfire/date-picker/lib';
import { HoverText } from '@campfire/hover-link/lib';
import { Select } from '@campfire/select';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { AddRounded, Close } from '@material-ui/icons';
import { Calendar, DateObject } from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import React, { Fragment, useMemo, useState } from 'react';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ActivityFormValues, ActivityFormSession, RecurrenceFrequency } from '../ActivityFormV2';
import { AddNewSessionDialogV2 } from '../add-new-session-dialog-v2/AddNewSessionDialogV2';
import { SimpleActivityRecurrence } from '../SimpleActivityRecurrence';

type EventDateMode = 'range' | 'individual';

interface FrequencyItem {
  label: string;
  value: RecurrenceFrequency;
}

function getPodiumPositionReadable(value: number): string {
  switch (value) {
    case 1:
      return 'first';
    case 2:
      return 'second';
    case 3:
      return 'third';
    case 4:
      return 'fourth';
    case 5:
      return 'fifth';
    default:
      return 'last';
  }
}

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const isOneOffSession = (session: ActivityFormSession): boolean => {
  return !!(session as any).isOneOff || !!(session as any).activityDate;
};

export const ScheduleSessionsStep = () => {
  const formik = useFormikContext<ActivityFormValues>();
  const { values, setFieldValue } = formik;
  const { activityType } = values;
  const { theme } = useCampfireTheme();
  const { darkTextColor } = getAlertCardColors('info');

  const [showEndDate, setShowEndDate] = useState(!!values.endDate);
  const [showingActivityRecurrence, setShowingActivityRecurrence] = useState(false);
  const [openAddNewSessionDialog, setOpenAddNewSessionDialog] = useState<{ status: boolean; idx?: number }>({
    status: false,
    idx: undefined,
  });
  const [selectedSession, setSelectedSession] = useState<ActivityFormSession>();

  const startDateTime = useMemo(() => (values.startDate ? DateTime.fromJSDate(values.startDate) : undefined), [
    values.startDate,
  ]);

  const frequencyItems: FrequencyItem[] = useMemo(() => {
    const numWeekdayInMonth = startDateTime
      ? Math.ceil(Number.parseInt(startDateTime.toFormat('d'), 10) / 7)
      : undefined;
    const numWeekdayPodiumPosition = getPodiumPositionReadable(numWeekdayInMonth ?? 1);
    const dayReadable = startDateTime ? startDateTime.toFormat('cccc') : undefined;

    return [
      { label: 'Once off', value: 'no-repeat' },
      { label: 'Daily', value: 'daily' },
      { label: dayReadable ? `Weekly on ${dayReadable}` : 'Weekly', value: 'weekly' },
      { label: 'Weekdays (Monday - Friday)', value: 'weekdays' },
      { label: 'Weekends (Saturday & Sunday)', value: 'weekends' },
      {
        label: startDateTime && dayReadable ? `Fortnightly on ${dayReadable}` : 'Fortnightly',
        value: 'fortnightly',
      },
      {
        label: startDateTime ? `Monthly on the ${getOrdinal(startDateTime.day)}` : 'Monthly',
        value: 'monthly-month-day',
      },
      {
        label: startDateTime ? `Monthly on the ${numWeekdayPodiumPosition} ${dayReadable}` : 'Monthly',
        value: 'monthly-nth-day',
      },
      { label: 'Yearly', value: 'yearly' },
      { label: 'Custom', value: 'custom' },
    ];
  }, [startDateTime]);

  const regularSessionsWithIndex = values.sessions
    .map((session, originalIndex) => ({ session, originalIndex }))
    .filter(({ session }) => !isOneOffSession(session));

  const error = getIn(formik.errors, 'sessions');
  const touched = getIn(formik.touched, 'sessions');

  const isSelfDirected = activityType === 'self-directed';
  const isEventType = activityType === 'event';
  const showFrequency = activityType === 'regular' || activityType === 'once-off';
  const frequencyDisabled = activityType === 'once-off';
  const displayedFrequencyItems =
    activityType === 'regular' ? frequencyItems.filter((item) => item.value !== 'no-repeat') : frequencyItems;
  const showStartDate = activityType === 'regular' || activityType === 'once-off';
  const showEndDateField = activityType === 'regular' && values.frequency !== 'no-repeat';
  const startDateRequired = activityType === 'regular' || activityType === 'once-off';
  const sessionLabel = isSelfDirected ? 'action' : 'session';
  const sessionLabelPlural = isSelfDirected ? 'Actions' : 'Sessions';

  const [eventDateMode, setEventDateMode] = useState<EventDateMode>('range');
  const [rangeStartDate, setRangeStartDate] = useState<Date | null>(null);
  const [rangeEndDate, setRangeEndDate] = useState<Date | null>(null);

  const generateDatesInRange = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const startDt = DateTime.fromJSDate(start).startOf('day');
    const endDt = DateTime.fromJSDate(end).startOf('day');
    let cursor = startDt;
    while (cursor <= endDt) {
      dates.push(cursor.toJSDate());
      cursor = cursor.plus({ days: 1 });
    }
    return dates;
  };

  const handleRangeStartChange = (date: Date | undefined) => {
    const newStart = date ?? null;
    setRangeStartDate(newStart);
    if (newStart && rangeEndDate && newStart <= rangeEndDate) {
      const dates = generateDatesInRange(newStart, rangeEndDate);
      setFieldValue('eventDates', dates);
      setFieldValue('startDate', newStart);
      setFieldValue('endDate', rangeEndDate);
    }
  };

  const handleRangeEndChange = (date: Date | undefined) => {
    const newEnd = date ?? null;
    setRangeEndDate(newEnd);
    if (rangeStartDate && newEnd && rangeStartDate <= newEnd) {
      const dates = generateDatesInRange(rangeStartDate, newEnd);
      setFieldValue('eventDates', dates);
      setFieldValue('startDate', rangeStartDate);
      setFieldValue('endDate', newEnd);
    }
  };

  const handleBulkDatesChange = (dates: DateObject[]) => {
    const jsDateArray = dates.map((d) => d.toDate());
    setFieldValue('eventDates', jsDateArray);
    if (jsDateArray.length > 0) {
      const sorted = [...jsDateArray].sort((a, b) => a.getTime() - b.getTime());
      setFieldValue('startDate', sorted[0]);
      setFieldValue('endDate', sorted[sorted.length - 1]);
    }
  };

  return (
    <Box>
      <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
        {isSelfDirected ? 'Actions' : 'Schedule & Sessions'}
      </Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
        {isSelfDirected
          ? 'Add the actions that make up this activity. Each action represents a task volunteers can complete, such as filling out a survey or submitting a report.'
          : isEventType
          ? 'Select the dates your event runs on, then add sessions for each shift or time slot. Sessions are repeated across each event day.'
          : activityType === 'regular'
          ? 'Set when this activity runs and how often, then add sessions for each shift.'
          : activityType === 'once-off'
          ? 'Set the date for this activity and add sessions.'
          : 'Add sessions to your flexible activity. Rosters are created manually as needed.'}
      </Typography>

      {showingActivityRecurrence && (
        <SimpleActivityRecurrence
          open
          onClose={() => setShowingActivityRecurrence(false)}
          onChange={(value) => setFieldValue('recurrence', value)}
          fullScreen={false}
          value={values.recurrence}
        />
      )}

      {isEventType && (
        <Box mb={3}>
          <Typography style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Event Dates</Typography>
          <Box mb={2}>
            <ToggleButtonGroup
              value={eventDateMode}
              exclusive
              onChange={(_unusedEvent, newMode) => {
                if (newMode !== null) {
                  setEventDateMode(newMode);
                  setFieldValue('eventDates', []);
                  setRangeStartDate(null);
                  setRangeEndDate(null);
                }
              }}
            >
              <ToggleButton
                value='range'
                style={
                  eventDateMode === 'range'
                    ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                    : { padding: '6px 10px' }
                }
              >
                <Typography style={{ fontWeight: 600, fontSize: 11 }}>Date Range</Typography>
              </ToggleButton>
              <ToggleButton
                value='individual'
                style={
                  eventDateMode === 'individual'
                    ? { backgroundColor: '#7047EB', color: '#fff', padding: '6px 10px' }
                    : { padding: '6px 10px' }
                }
              >
                <Typography style={{ fontWeight: 600, fontSize: 11 }}>Pick Dates</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {eventDateMode === 'range' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  fullWidth
                  variant='inline'
                  format='dd/MM/yyyy'
                  value={rangeStartDate}
                  label='Start Date'
                  inputVariant='outlined'
                  onChange={(date) => handleRangeStartChange(date?.toJSDate())}
                  autoOk
                  placeholder='dd/mm/yyyy'
                  allowKeyboardControl
                  InputAdornmentProps={{ position: 'start' }}
                  KeyboardButtonProps={{ tabIndex: -1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  fullWidth
                  variant='inline'
                  format='dd/MM/yyyy'
                  value={rangeEndDate}
                  label='End Date'
                  inputVariant='outlined'
                  onChange={(date) => handleRangeEndChange(date?.toJSDate())}
                  autoOk
                  placeholder='dd/mm/yyyy'
                  allowKeyboardControl
                  InputAdornmentProps={{ position: 'start' }}
                  KeyboardButtonProps={{ tabIndex: -1 }}
                  minDate={rangeStartDate ?? undefined}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='caption' style={{ fontWeight: 700 }}>
                  {values.eventDates.length} date{values.eventDates.length !== 1 ? 's' : ''} selected
                </Typography>
              </Grid>
            </Grid>
          )}

          {eventDateMode === 'individual' && (
            <Box>
              <Calendar
                multiple
                format='DD MMM YY'
                minDate={DateTime.local().toFormat('yyyy-MM-dd')}
                value={values.eventDates.map((d) => new DateObject(d))}
                onChange={handleBulkDatesChange}
                className='multi-date-picker'
                plugins={[<DatePanel position='right' sort='date' />]}
              />
              <Box display='flex' justifyContent='flex-end' mt={1}>
                <Typography variant='caption' style={{ fontWeight: 700 }}>
                  {values.eventDates.length} date{values.eventDates.length !== 1 ? 's' : ''} selected
                </Typography>
              </Box>
            </Box>
          )}

          <Box mt={2}>
            <AlertCard variant='info' title='Managing event dates'>
              <Typography style={{ fontSize: 14, fontWeight: 400, color: darkTextColor }}>
                Once created, event dates are managed from the Rosters page. A roster template will be created for each
                date with all sessions.
              </Typography>
            </AlertCard>
          </Box>
        </Box>
      )}

      <Grid container spacing={3}>
        {showStartDate && (
          <Grid item xs={12} sm={6}>
            <DatePickerField
              name='startDate'
              label='Start Date'
              autoOk
              fullWidth
              inputVariant='outlined'
              required={startDateRequired}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}

        {showEndDateField && (
          <Grid item xs={12} sm={6}>
            {showEndDate ? (
              <DatePickerField
                name='endDate'
                label='End Date (Optional)'
                autoOk
                fullWidth
                inputVariant='outlined'
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box pt={2}>
                <HoverText variant='body2' color='primary' onClick={() => setShowEndDate(true)}>
                  Add an end date
                </HoverText>
              </Box>
            )}
          </Grid>
        )}

        {showFrequency && (
          <Grid item xs={12} sm={6}>
            <Select
              disabled={frequencyDisabled}
              variant='outlined'
              helper
              slow
              required
              native
              shrink
              fullWidth
              name='frequency'
              label='Frequency'
              items={displayedFrequencyItems}
              renderFunction={(frequency: FrequencyItem) => frequency.label}
              valueFunction={(frequency: FrequencyItem) => frequency.value}
            />
            {values.frequency === 'custom' && (
              <Box mt={1}>
                <HoverText color='primary' variant='body2' onClick={() => setShowingActivityRecurrence(true)}>
                  Edit custom recurrence
                </HoverText>
              </Box>
            )}
          </Grid>
        )}
      </Grid>

      <Box mt={4}>
        <Typography style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{sessionLabelPlural}</Typography>

        <FieldArray
          name='sessions'
          render={(arrayHelpers) => (
            <Fragment>
              {regularSessionsWithIndex.length > 0 && (
                <Grid container spacing={1} style={{ marginBottom: 8 }}>
                  {regularSessionsWithIndex.map(({ session, originalIndex }) => (
                    <Grid
                      item
                      container
                      xs={12}
                      alignItems='center'
                      key={session.sessionId || `${session.name}-${originalIndex}`}
                    >
                      <Grid
                        item
                        xs={11}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedSession(session);
                          setOpenAddNewSessionDialog({ status: true, idx: originalIndex });
                        }}
                      >
                        <Box border={`1px solid ${theme.color.grey.border}`} borderRadius={8} px={2} py={1.5} mb={1}>
                          <Typography style={{ fontWeight: 600, fontSize: 14 }}>{session.name}</Typography>
                          <Typography variant='body2' color='textSecondary'>
                            {isSelfDirected ? (
                              session.remoteLocation?.details || 'No external link set'
                            ) : (
                              <>
                                {session.startTime
                                  ? `${DateTime.fromJSDate(session.startTime).toFormat('h:mm a')} - ${
                                      session.endTime ? DateTime.fromJSDate(session.endTime).toFormat('h:mm a') : '...'
                                    }`
                                  : 'No time set'}
                                {session.minVolunteers ? ` · Min ${session.minVolunteers}` : ''}
                                {session.maxVolunteers ? ` · Max ${session.maxVolunteers}` : ''}
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={1} style={{ textAlign: 'center' }}>
                        <IconButton size='small' onClick={() => arrayHelpers.remove(originalIndex)}>
                          <Close fontSize='small' />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}

              <TabletButton
                startIcon={<AddRounded />}
                color={error && touched ? 'error' : 'primary'}
                variant='contained'
                onClick={() => {
                  setSelectedSession(undefined);
                  setOpenAddNewSessionDialog({ status: true });
                }}
                style={{ textTransform: 'none' }}
              >
                {regularSessionsWithIndex.length === 0 ? `Add ${sessionLabel}` : `Add another ${sessionLabel}`}
              </TabletButton>

              {(activityType === 'regular' || activityType === 'flexible') && (
                <Box mt={2}>
                  <AlertCard variant='info' title='One-off sessions'>
                    <Typography style={{ fontSize: 14, fontWeight: 400, color: darkTextColor }}>
                      You can create one-off sessions and customise rosters from the roster management screen after
                      creating this activity.
                    </Typography>
                  </AlertCard>
                </Box>
              )}
            </Fragment>
          )}
        />
      </Box>

      {openAddNewSessionDialog.status && (
        <AddNewSessionDialogV2
          open={openAddNewSessionDialog.status}
          session={selectedSession}
          idx={openAddNewSessionDialog.idx}
          close={() => setOpenAddNewSessionDialog({ status: false })}
        />
      )}
    </Box>
  );
};
